import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';

// Lazy load emoji-mart to reduce initial bundle size (~500KB)
const EmojiPicker = lazy(() => import('./FullEmojiPicker'));

interface ReactionPickerProps {
  show: boolean;
  commonEmojis: string[];
  messageReactions?: Array<{ emoji: string; user_reacted: boolean }>;
  onSelect: (emoji: string, currentlyReacted: boolean) => void;
  onClose: () => void;
  position?: 'left' | 'right';
  triggerRef?: React.RefObject<HTMLElement | null>;
}

interface EmojiData {
  native: string;
  id: string;
  name: string;
}

/**
 * Compact inline reaction picker following Slack/Discord patterns.
 * Shows 6 "quick reactions" in a pill-shaped container with smooth animations.
 * Includes a "+" button to open the full emoji picker.
 * Uses a portal to render outside scroll containers (avoids overflow clipping).
 *
 * Design principles:
 * - Compact: 28px buttons on desktop, 36px touch targets on mobile
 * - Quick access: Most common reactions readily available
 * - Full picker: Access to all emojis via "+" button
 * - Responsive: Adapts to touch vs. pointer devices
 * - Accessible: Keyboard navigable, proper ARIA labels
 */
export function ReactionPicker({
  show,
  commonEmojis,
  messageReactions = [],
  onSelect,
  onClose,
  position = 'left',
  triggerRef,
}: ReactionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const fullPickerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [showFullPicker, setShowFullPicker] = useState(false);

  // Calculate position based on trigger element
  useEffect(() => {
    if (!show || !triggerRef?.current) {
      setCoords(null);
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    // Position above the trigger, offset by picker height (~40px) plus margin
    const top = rect.top - 44;
    // Align left or right based on position prop
    const left = position === 'right' ? rect.right : rect.left;

    setCoords({ top, left });
  }, [show, triggerRef, position]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideQuickPicker = pickerRef.current && !pickerRef.current.contains(target);
      const clickedOutsideFullPicker =
        fullPickerRef.current && !fullPickerRef.current.contains(target);

      // If full picker is open, only close if clicking outside both
      if (showFullPicker) {
        if (clickedOutsideQuickPicker && clickedOutsideFullPicker) {
          setShowFullPicker(false);
          onClose();
        }
      } else {
        if (clickedOutsideQuickPicker) {
          onClose();
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showFullPicker) {
          setShowFullPicker(false);
        } else {
          onClose();
        }
      }
    };

    // Delay adding listener to avoid immediate close from the click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, showFullPicker, onClose]);

  // Reset full picker when main picker closes
  useEffect(() => {
    if (!show) {
      setShowFullPicker(false);
    }
  }, [show]);

  // Take only first 6 emojis for compact quick-reactions (Slack pattern)
  const quickReactions = commonEmojis.slice(0, 6);

  const handleFullPickerSelect = (emoji: EmojiData) => {
    const currentlyReacted =
      messageReactions.find((r) => r.emoji === emoji.native)?.user_reacted ?? false;
    onSelect(emoji.native, currentlyReacted);
    setShowFullPicker(false);
  };

  const quickPicker = (
    <div
      ref={pickerRef}
      className={`
        fixed
        bg-slate-800/95 backdrop-blur-sm
        border border-slate-600/50
        rounded-full shadow-lg
        flex items-center gap-0.5 p-1
        z-50 pointer-events-auto
        transition-all duration-150 ease-out
        ${show && coords ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}
      `}
      style={
        coords
          ? {
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: position === 'right' ? 'translateX(-100%)' : undefined,
            }
          : { top: '-9999px', left: '-9999px' }
      }
      role="listbox"
      aria-label="Quick reactions"
    >
      {quickReactions.map((emoji, index) => {
        const userReacted = messageReactions.find((r) => r.emoji === emoji)?.user_reacted ?? false;
        return (
          <button
            key={emoji}
            onClick={() => onSelect(emoji, userReacted)}
            className={`
              w-7 h-7 sm:w-8 sm:h-8
              flex items-center justify-center
              rounded-full
              text-base sm:text-lg
              transition-all duration-100
              hover:scale-110 hover:bg-slate-700
              active:scale-95
              focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 focus:ring-offset-slate-800
              ${userReacted ? 'bg-blue-500/30 ring-1 ring-blue-400/50' : ''}
            `}
            title={emoji}
            aria-label={`React with ${emoji}`}
            aria-selected={userReacted}
            role="option"
            tabIndex={show ? 0 : -1}
            style={{ animationDelay: `${index * 20}ms` }}
          >
            {emoji}
          </button>
        );
      })}

      {/* Divider */}
      <div className="w-px h-5 bg-slate-600/50 mx-0.5" />

      {/* "More" button to open full picker */}
      <button
        onClick={() => setShowFullPicker(!showFullPicker)}
        className={`
          w-7 h-7 sm:w-8 sm:h-8
          flex items-center justify-center
          rounded-full
          text-slate-400
          transition-all duration-100
          hover:scale-110 hover:bg-slate-700 hover:text-slate-200
          active:scale-95
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 focus:ring-offset-slate-800
          ${showFullPicker ? 'bg-slate-700 text-slate-200' : ''}
        `}
        title="More reactions"
        aria-label="More reactions"
        aria-expanded={showFullPicker}
        tabIndex={show ? 0 : -1}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4"
        >
          <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
        </svg>
      </button>
    </div>
  );

  // Calculate full picker position - prefer above, but use below if not enough space
  const getFullPickerPosition = () => {
    if (!coords) return { top: '-9999px', left: '-9999px' };

    const pickerHeight = 380;
    const spaceAbove = coords.top - 44; // 44px is quick picker height
    const spaceBelow = window.innerHeight - coords.top - 44;

    // If not enough space above, position below
    if (spaceAbove < pickerHeight && spaceBelow >= pickerHeight) {
      return {
        top: `${coords.top + 44}px`,
        left: `${coords.left}px`,
      };
    }

    // Default: position above, but clamp to viewport
    const topPosition = Math.max(8, coords.top - pickerHeight);
    return {
      top: `${topPosition}px`,
      left: `${coords.left}px`,
    };
  };

  // Full emoji picker (Slack-style) - lazy loaded
  const fullPicker = showFullPicker && coords && (
    <div
      ref={fullPickerRef}
      className="fixed z-[60] pointer-events-auto"
      style={{
        ...getFullPickerPosition(),
        transform: position === 'right' ? 'translateX(-100%)' : undefined,
      }}
    >
      <Suspense
        fallback={
          <div className="w-[352px] h-[380px] bg-slate-800 rounded-xl flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-slate-600 border-t-blue-500 rounded-full" />
          </div>
        }
      >
        <EmojiPicker onSelect={handleFullPickerSelect} />
      </Suspense>
    </div>
  );

  // Render via portal to avoid overflow clipping from scroll containers
  return createPortal(
    <>
      {quickPicker}
      {fullPicker}
    </>,
    document.body
  );
}
